import React from 'react'
import { useRadioGroup } from 'react-aria'
import { useRadioGroupState } from 'react-stately'
import invariant from 'tiny-invariant'
import { typeValidator } from '@chilipiper/utils'
import { PositioningProps } from '../../../types'
import { Box } from '../../../../core-components'
import { validatePositioningProps } from '../../../../helpers/validatePositioningProps'
import { RadioBaseProvider } from '../RadioContext'
import { RadioItemPublic as RadioItem } from '../radio-item/RadioItem'
import { SettingLabel, SettingLabelContainer } from '../../../wayfinding/setting-label/SettingLabel'
import { useGroup } from '../../../wayfinding/setting-label/hooks'

type Props = {
  children: React.ReactNode
  defaultValue?: string
  label: React.ReactNode
  name: string
  onChange?: (value: string) => void
  value?: string
  isRequired?: boolean
} & PositioningProps

export const RadioGroup = ({
  value,
  defaultValue,
  name,
  children,
  onChange,
  label,
  isRequired = false,
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'RadioGroup')

  const state = useRadioGroupState({ name, value, onChange, label, defaultValue })
  // We're not using react-aria's useRadioGroup because we need to be able to show expandable content inline
  // See https://github.com/adobe/react-spectrum/issues/2379
  // We're only using it to register radioGroupData otherwise we'd be destructuring undefined here
  // https://github.com/adobe/react-spectrum/blob/5e49ce79094a90839cec20fc5ce788a34bf4b085/packages/%40react-aria/radio/src/useRadio.ts#L94
  useRadioGroup(
    {
      label:
        'just to ignore the warning' /* https://github.com/adobe/react-spectrum/blob/5e49ce79094a90839cec20fc5ce788a34bf4b085/packages/%40react-aria/label/src/useLabel.ts#L56 */,
    },
    state
  )
  const { groupProps, labelProps, descriptionProps } = useGroup()
  invariant(typeValidator(RadioLabel)(label), 'Radio.Group: label must be a Radio.Label')
  invariant(
    React.Children.toArray(children).every(typeValidator(RadioItem)),
    'Radio.Group: all children must be Radio.Item components'
  )
  return (
    <RadioBaseProvider value={{ name, state }}>
      <SettingLabelContainer
        label={label}
        labelElement='div'
        isRequired={isRequired}
        labelProps={labelProps}
        descriptionProps={descriptionProps}
        {...positioningProps}
      >
        <Box
          bg='bg/section'
          boxShadow='border/section'
          borderRadius={8}
          overflow='hidden'
          aria-required={isRequired || undefined}
          {...groupProps}
        >
          {children}
        </Box>
      </SettingLabelContainer>
    </RadioBaseProvider>
  )
}

export const RadioLabel = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)
